import React, { useState, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import CloseButton from 'components/CustomButtons/CloseButton';
import { previousModalPage } from 'actions/modalPage';
import InputField from 'components/FieldTypes/InputField';
import styles from 'components/FieldTypes/skins/skins.module.scss';
import SaveButton from 'components/CustomButtons/SaveButton';
import { addProduct } from 'actions/ShoppingCart';
import { useShortcut } from 'utils/hooks/keyboard/useShortcut';
import { sanitizePrice } from 'actions/CreateNew';
import { round } from 'utils';
import { getShowPricesWithTax } from 'reducers/configs/settings';
import { getHasRightToReturnWithoutReceipt } from 'reducers/Login';
import { addError } from 'actions/Error';
import { useAppDispatch } from 'reducers';
import { Product } from 'types/Product';

type Props = {
  product: Product;
  productID: Product['productID'];
  giftCardSerial?: string;
  allowNegativePrice?: boolean;
};

function CashierPrice({
  product,
  productID,
  giftCardSerial = '',
  allowNegativePrice = true,
}: Props) {
  const dispatch = useAppDispatch();
  const isWithTax = useSelector(getShowPricesWithTax);
  const [price, setPrice] = useState(
    `${round(isWithTax ? product.priceWithVat : product.price)}`,
  );
  const hasRightToReturnWithoutReceipt = useSelector(
    getHasRightToReturnWithoutReceipt,
  );

  const calculateNoVat = (value, vat) => {
    return (value * 100) / (vat + 100);
  };

  const handleSubmit = async () => {
    if (price && price !== '' && Number(price) !== 0) {
      if (Number(price) < 0 && !hasRightToReturnWithoutReceipt) {
        dispatch(
          addError(
            i18next.t('shoppingCart:alerts.unreferencedReturnForbidden'),
          ),
        );
        return;
      }
      const priceWithoutVAT = isWithTax
        ? calculateNoVat(price, product.vatrate)
        : price;
      dispatch(previousModalPage());
      dispatch(
        addProduct({
          productID,
          giftCardSerial,
          priceSet: true,
          basePrice: priceWithoutVAT,
        }),
      );
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitizedPrice = sanitizePrice(e.target.value);
    const finalPrice = allowNegativePrice
      ? sanitizedPrice
      : sanitizedPrice.replace('-', '');

    setPrice(finalPrice);
  };

  const handleClose = () => {
    return dispatch(previousModalPage());
  };

  useShortcut('cancel', handleClose);
  const { t } = useTranslation();
  return (
    <div className="cashier-price" data-testid="cashier-price">
      <Modal.Header>
        <Modal.Title>
          <h3 data-testid="product-name">
            <b>{`${product.name} ${product.code}`}</b>
          </h3>
        </Modal.Title>
        <div
          className="d-flex align-content-center"
          style={{ marginLeft: 'auto' }}
        >
          <SaveButton
            className="assign-serial-button"
            action={handleSubmit}
            disabled={!price || price === '' || Number(price) === 0}
            data-testid="save"
          />
          <CloseButton
            style={{ transform: 'scale(0.85)' }}
            action={handleClose}
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.stopPropagation();
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Form.Group>
            <InputField
              size="lg"
              title={t('common:glossary.price')}
              value={price}
              name="cashier-price"
              type="text"
              autoFocus
              onChange={handleChange}
              className={classnames(styles.formInput, styles.mediumTitle)}
              data-testid="price-field"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
    </div>
  );
}

export default CashierPrice;
